"use client";
import CardSlide from "./CardSlide";
import {
  FaAnglesLeft,
  FaAnglesRight,
  FaRegEye,
  FaXmark,
} from "react-icons/fa6";
import { useEffect, useState } from "react";
import Img from "./Img";
import { getImage, getImageIdsWithType } from "@/utils/data/image";

type Props = {
  imageType: string;
  imagesHeight?: number;
};

export default function Gallery({ imageType, imagesHeight = 400 }: Props) {
  const [shownImageIndex, setShownImageIndex] = useState<number | null>(null);
  const [isImageBlurred, setIsImageBlurred] = useState(false);
  const [isLeftButtonActive, setIsLeftButtonActive] = useState(false);
  const [isRightButtonActive, setIsRightButtonActive] = useState(true);

  const imageIds = getImageIdsWithType(imageType);

  useEffect(() => {
    setIsImageBlurred(false);
    setIsLeftButtonActive(shownImageIndex !== null && shownImageIndex > 0);
    setIsRightButtonActive(
      shownImageIndex !== null && shownImageIndex < imageIds.length - 1
    );
  }, [shownImageIndex]);

  const showImage = (index: number) => {
    setShownImageIndex(index);
  };

  const hideImage = () => {
    setShownImageIndex(null);
  };

  const changeImage = (inc: -1 | 1) => {
    if (shownImageIndex === null) {
      return;
    }

    const newShownImageIndex = shownImageIndex + inc;
    if (newShownImageIndex < 0 || newShownImageIndex > imageIds.length - 1) {
      return;
    }

    setIsImageBlurred(true);
    setTimeout(() => {
      showImage(newShownImageIndex);
    }, 300);
  };

  return (
    <div>
      <CardSlide>
        {imageIds.map((imageid, i) => {
          return (
            <div
              key={i}
              className="overflow-hidden shrink-0 relative mr-12 rounded-4xl shadow-md transition-all outline-0 outline-primary/50 focus-within:outline-10"
              style={{ height: imagesHeight }}
            >
              <Img
                id={imageid}
                classImage="object-cover w-full h-full"
                height={imagesHeight}
              />

              <button
                className="absolute inset-0 flex flex-col justify-center items-center bg-primary/50 text-white transition-opacity outline-0 opacity-0 hover:opacity-100 focus:opacity-100"
                onClick={() => showImage(i)}
              >
                <FaRegEye className="mb-4 text-4xl" />
                <p className="text-lg tracking-wider">Megnézem</p>
              </button>
            </div>
          );
        })}
      </CardSlide>

      {shownImageIndex !== null && (
        <div
          className="z-1001 overflow-y-auto overflow-x-hidden fixed inset-0 bg-black/90 flex flex-col justify-start items-center lg:justify-center"
          onClick={(e) => {
            if (
              e.target instanceof HTMLDivElement &&
              e.target.matches("div.z-1001")
            ) {
              hideImage();
            }
          }}
        >
          <button
            className="button-outlined-white absolute top-4 right-4"
            aria-label="Kép bezárása"
            onClick={hideImage}
          >
            <FaXmark />
          </button>

          <div className="hidden absolute left-1/8 flex justify-center items-center lg:block">
            <button
              className="button-outlined-white"
              disabled={!isLeftButtonActive}
              aria-label="Előző kép betöltése"
              onClick={() => changeImage(-1)}
            >
              <FaAnglesLeft size={20} />
            </button>
          </div>

          <Img
            id={imageIds[shownImageIndex]}
            classImage={`block object-contain m-4 w-auto max-w-[85vw] max-h-[70vh] rounded-4xl transition-filter 
              ${isImageBlurred ? "blur-xs" : ""} 
              lg:mb-6 lg:max-w-full
            `}
          />

          <div
            className="
            flex justify-around items-center w-full
            lg:absolute lg:bottom-auto lg:right-1/8 lg:justify-center lg:w-auto"
          >
            <button
              className="button-outlined-white lg:hidden"
              disabled={!isLeftButtonActive}
              aria-label="Előző kép betöltése"
              onClick={() => changeImage(-1)}
            >
              <FaAnglesLeft size={20} />
            </button>

            <button
              className="button-outlined-white"
              disabled={!isRightButtonActive}
              aria-label="Következő kép betöltése"
              onClick={() => changeImage(1)}
            >
              <FaAnglesRight size={20} />
            </button>
          </div>

          <p className="max-w-xl p-4 text-center text-white" aria-hidden="true">
            {getImage(imageIds[shownImageIndex])?.alt}
          </p>
        </div>
      )}
    </div>
  );
}
